import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { getRoles as fetchRolesApi, deleteRole as deleteRoleApi } from '../../../models/roleModel';
import Swal from 'sweetalert2';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

// Debounced Input for Global Filter
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (value !== initialValue) {
        onChange(value);
      }
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, initialValue, onChange, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}


function RolesList() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can } = useAuth();
  


  const initialPagination = () => {
    const storedPagination = localStorage.getItem('rolesTablePagination');
    if (storedPagination) {
      return JSON.parse(storedPagination);
    }
    return {
      pageIndex: 0,
      pageSize: 10,
    };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);

  useEffect(() => {
    localStorage.setItem('rolesTablePagination', JSON.stringify(pagination));
  }, [pagination]);

  const columns = useMemo(() => [
    {
      accessorKey: 'role_name',
      header: 'Name',
      cell: info => <span className="font-medium text-gray-800">{info.getValue()}</span>,
    },
    {
      accessorKey: 'permissions',
      header: 'Permissions',
      cell: info => {
        try {
          const perms = JSON.parse(info.getValue() || '[]');
          return (
            <div className="flex flex-wrap gap-1 max-w-md">
              {Array.isArray(perms) && perms.length > 0 ? perms.map((permission, index) => (
                <span 
                  key={index} 
                  className="px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full"
                >
                  {permission}
                </span>
              )) : <span className="text-gray-400 text-xs">No permissions</span>}
            </div>
          );
        } catch (e) {
          return <span className="text-red-600 text-xs">Error parsing permissions</span>;
        }
      },
      enableSorting: false,
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <div className="flex items-center space-x-1">
          <Link
            to={`edit/${row.original.role_id}`}
            className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
          >
            Edit
          </Link>
          {can('Delete Roles') && (
            <button
              type="button"
              className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
              onClick={() => handleDelete(row.original.role_id, row.original.role_name)}
            >
              Delete
            </button>
          )}
        </div>
      ),
      enableSorting: false,
    },
  ], [can]);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.pageIndex + 1);
      params.append('pageSize', pagination.pageSize);
      if (globalFilter) {
        params.append('filter', globalFilter);
      }
      if (sorting.length > 0) {
        params.append('sortBy', sorting[0].id);
        params.append('sortOrder', sorting[0].desc ? 'DESC' : 'ASC');
      }
       
      const response = await fetchRolesApi(params);
      if (response.status === 1 && response.data && response.data.rows && response.data.meta) {
        setRoles(response.data.rows);
        setRowCount(response.data.meta.totalItems);
      } else {
        toast.error(response.message || 'Failed to load roles');
        setRoles([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error(error.message || 'An error occurred while loading roles');
      setRoles([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, globalFilter, sorting]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);


  const table = useReactTable({
    data: roles,
    columns,
    state: {
      pagination,
      globalFilter,
      sorting,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    debugTable: false, 
  });

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete the role "${name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--color-accent-danger)',
      cancelButtonColor: 'var(--color-neutral-500)',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteRoleApi(id);
          if (response.status === 1) {
            toast.success(`Role "${name}" deleted successfully!`);
            fetchRoles();
          } else {
            toast.error(response.message || 'Failed to delete role');
          }
        } catch (error) {
          console.error('Error deleting role:', error);
          toast.error(error.response?.data?.message || 'An error occurred while deleting the role');
        }
      }
    });
  };

  if (loading && roles.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
            <div className="flex justify-center items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading roles...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Roles Management</h1>
                <p className="text-xs text-gray-500 mb-1">Manage system roles and permissions</p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {rowCount} Total Roles
                  </span>
                  {pagination.pageIndex > 0 && (
                    <span className="text-xs text-gray-400 ml-2">
                      • Page {pagination.pageIndex + 1}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Right: Controls and Actions */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2">
                {/* Refresh Button */}
                <button
                  onClick={() => fetchRoles()}
                  disabled={loading}
                  className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  title="Refresh"
                >
                  <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                
                {/* Search Input */}
                <DebouncedInput
                  value={globalFilter ?? ''}
                  onChange={value => {
                    setGlobalFilter(String(value));
                    setPagination(p => ({ ...p, pageIndex: 0 }));
                  }}
                  className="flex-1 min-w-0 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search roles..."
                />
                
                {/* Create Button */}
                {can('Create Roles') && (
                  <Link
                    to="create"
                    className="inline-flex items-center px-3 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                    style={{ 
                      backgroundColor: '#0087D4', 
                      boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                    }}
                  >
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Create New Role
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      scope="col"
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                      }`}
                      style={{ color: '#0087D4' }}
                      onClick={header.column.getToggleSortingHandler()}
                      title={
                        header.column.getCanSort() 
                          ? header.column.getNextSortingOrder() === 'asc' 
                            ? 'Sort ascending' 
                            : header.column.getNextSortingOrder() === 'desc' 
                            ? 'Sort descending' 
                            : 'Clear sort' 
                          : undefined
                      }
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <span className="ml-1 text-blue-600">↑</span>,
                        desc: <span className="ml-1 text-blue-600">↓</span>,
                      }[header.column.getIsSorted()] ?? null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && roles.length > 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-sm">Loading...</span>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && table.getRowModel().rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-6 text-center">
                    <p className="text-gray-600">
                      {globalFilter ? "No roles found matching your search." : "No roles found"}
                    </p>
                  </td>
                </tr>
              )}
              {!loading && table.getRowModel().rows.map(row => (
                <tr key={row.id} className="hover:bg-blue-50">
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="px-3 py-2 text-sm">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rowCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              {/* Results info */}
              <div className="text-gray-500">
                Showing {(pagination.pageIndex * pagination.pageSize) + 1} to {Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount)} of {rowCount} results
              </div>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 30, 40, 50].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              {/* Navigation buttons */}
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Current page indicator */}
              <div className="flex items-center space-x-1">
                <span className={`px-3 py-1.5 border rounded text-xs font-medium bg-blue-600 text-white border-blue-600`}>
                  {pagination.pageIndex + 1}
                </span>
              </div>
              
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default RolesList;
